import type { ReactNode } from 'react';

import {
  SEARCH_RESULT_ROWS_BEFORE_INLINE_AI_OVERVIEW,
  SEARCH_RESULT_ROWS_BEFORE_INLINE_NL_SUGGESTIONS,
  SEARCH_RESULT_ROWS_BEFORE_INLINE_ONBOARDING,
} from 'bundles/search-common/constants';
import type { SearchHit, SearchUpsellHit } from 'bundles/search-common/providers/searchTypes';
import { isSearchProductHit, isSearchUpsellHit } from 'bundles/search-common/utils/utils';

export type SearchResultInlineModule = {
  id: string;
  afterRenderedCardCount: number;
  content: ReactNode;
};

export const getRenderedResultGridCardCount = (hits: Array<SearchHit | SearchUpsellHit>): number =>
  hits.filter((hit) => isSearchProductHit(hit) || isSearchUpsellHit(hit)).length;

type GetInlineModuleLocalCardCountArgs = {
  requestedCardCount: number;
  renderedCardOffset: number;
  sectionRenderedCardCount: number;
  totalRenderedCardCount: number;
  clampToAvailableCards?: boolean;
};

export const getInlineModuleLocalCardCount = ({
  requestedCardCount,
  renderedCardOffset,
  sectionRenderedCardCount,
  totalRenderedCardCount,
  clampToAvailableCards = true,
}: GetInlineModuleLocalCardCountArgs): number | undefined => {
  const insertionPoint = clampToAvailableCards
    ? Math.min(requestedCardCount, totalRenderedCardCount)
    : requestedCardCount;
  const sectionEnd = renderedCardOffset + sectionRenderedCardCount;
  if (insertionPoint <= renderedCardOffset || insertionPoint > sectionEnd) return undefined;
  return insertionPoint - renderedCardOffset;
};

type GetSectionInlineModulesArgs = {
  cardsPerRow: number;
  renderedCardOffset: number;
  sectionRenderedCardCount: number;
  totalRenderedCardCount: number;
  clampToAvailableCards: boolean;
  aiOverview?: ReactNode;
  nlSuggestions?: ReactNode;
  onboarding?: ReactNode;
};

export const getSectionInlineModules = ({
  cardsPerRow,
  aiOverview,
  nlSuggestions,
  onboarding,
  ...placement
}: GetSectionInlineModulesArgs): SearchResultInlineModule[] => {
  const candidates = [
    { id: 'ai-overview', content: aiOverview, rowsBefore: SEARCH_RESULT_ROWS_BEFORE_INLINE_AI_OVERVIEW },
    { id: 'nl-suggestions', content: nlSuggestions, rowsBefore: SEARCH_RESULT_ROWS_BEFORE_INLINE_NL_SUGGESTIONS },
    { id: 'onboarding', content: onboarding, rowsBefore: SEARCH_RESULT_ROWS_BEFORE_INLINE_ONBOARDING },
  ];

  return candidates.flatMap(({ id, content, rowsBefore }) => {
    if (!content) return [];
    const afterRenderedCardCount = getInlineModuleLocalCardCount({
      ...placement,
      requestedCardCount: cardsPerRow * rowsBefore,
    });
    return afterRenderedCardCount === undefined ? [] : [{ id, afterRenderedCardCount, content }];
  });
};

export const getInlineModuleInsertionPoints = (
  inlineModules: SearchResultInlineModule[] | undefined,
  renderedCardCount: number
): Map<number, SearchResultInlineModule[]> => {
  const insertionPoints = new Map<number, SearchResultInlineModule[]>();
  if (!inlineModules?.length || renderedCardCount === 0) return insertionPoints;

  inlineModules.forEach((inlineModule) => {
    const insertionPoint = Math.min(Math.max(inlineModule.afterRenderedCardCount, 1), renderedCardCount);
    const modulesAtInsertionPoint = insertionPoints.get(insertionPoint) ?? [];
    modulesAtInsertionPoint.push(inlineModule);
    insertionPoints.set(insertionPoint, modulesAtInsertionPoint);
  });

  return insertionPoints;
};
